import _ from "lodash";
import Promise from "bluebird";
import inject from "scripts/ioc/inject";
import HybridPageableMediaCollection from "scripts/collections/hybridPageableMediaCollection";

class CurationMediaCollection extends HybridPageableMediaCollection {
  constructor(models, options, cacheService = inject("curationMediaCacheService")) {
    super(models, options);

    this.mediaId = options.mediaId;
    this.cacheService = cacheService;
  }

  /**
   * Stale-while-revalidate fetch. When the curation's media is already cached
   * (in-memory, per session) we hydrate the collection instantly and refresh in
   * the background; otherwise we fetch normally and prime the cache.
   */
  fetch(options) {
    if (this.cacheService.has(this.mediaId)) {
      this.reset(this.cacheService.get(this.mediaId), { parse: false });
      this.revalidate();

      return Promise.resolve(this);
    }

    return super.fetch(options).then(() => {
      this.cacheService.set(this.mediaId, this.toJSON());
      return this;
    });
  }

  /**
   * Background refresh used after a cache hit. `reset: true` reflects the server
   * ORDER (this collection has no comparator) and fires a single "reset" rather
   * than per-item "remove" events, so it won't disturb count listeners. A failed
   * refresh keeps the existing (stale) cache.
   */
  revalidate() {
    return super
      .fetch({ reset: true })
      .then(() => {
        this.cacheService.set(this.mediaId, this.toJSON());
        this.trigger("revalidated");
      })
      .catch(() => {
        // keep stale cache on network failure
      });
  }

  /**
   * Keep the cache in step with in-page edits (reorder / remove) so navigating
   * away and back shows the edited state immediately, ahead of revalidation.
   */
  syncCache(models) {
    this.cacheService.set(this.mediaId, models.map(model => model.toJSON()));
  }

  /**
   * Filter out media without thumbnails. Currently,
   * audio content has no thumbnail.
   */
  thumbnailModels() {
    return _.filter(this.models, curationMediaModel => {
      return !(
        curationMediaModel.get("mediaType") === "content" &&
        curationMediaModel.get("type") === "AUDIO"
      );
    });
  }
}

export default CurationMediaCollection;
